import {useModule} from '@/hooks/useModule';
import {useAction, useActions, useKysely, useSelectors, useSelectorWithRefresh} from '@/store/kysely_module';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useSignals} from '@preact/signals-react/runtime';

// import {SectionView} from '../components/section_view';
import {CommentSection} from '../components/comments/comment_section';
import {MirtPlayer} from '../components/mirt_player';
import {IconPencil, IconPlayerPause, IconPlayerPlay, IconStar, IconStarFilled} from '@tabler/icons-react';
import {useIsMobile, useIsWideScreen} from '@/utils/user_agent';
import {useHover} from '@mantine/hooks';
import {useModals} from '@/modules/music_sniper/components/modals/modals_provider';
import {notifications} from '@mantine/notifications';
import {signalAudioSrc} from '@/components/audio_player';
import {RegionWithComment, WavesurferWithHook} from '@/components/wavesurfer_with_hook';
import {getRegionsForComments, getSecondsFromTimestamp, parseTimestampsWithRegions} from '@/utils/timestamp_parsing';
import {AttributesBlocksProps, AttributesBlocksBody, AttributesBlocksHeader, useAttributesBlocksState} from '@/components/attribute_editors';
import {PlayIcon} from '@/modules/audio_player/audio_player_module';
import {getVideoMimeTypeFromFileName, isAudioFile, isVideoFile} from '@/utils/file_utils';
import {DBObjects} from '@/store/store_types';
import {useCurrentUser} from '../hooks/useCurrentUser';
import {useOptimistic} from '@/hooks/useOptimistic';

type Props = {
    file_id: string;
    workspace_id: string;
}

export const FileViewPage = (props: Props) => {
    useSignals();

    const audioSrc = signalAudioSrc.value;

    const [videoFileTimestamp, setVideoFileTimestamp] = useState<{timestamp: string} | null>(null);

    const [remoteFileInfo, refreshFileInfo, loading, error] = useSelectorWithRefresh('getFileInfo', {file_id: props.file_id}, null, {workspace_id: props.workspace_id});
    const [fileInfo, setFileInfo] = useOptimistic(remoteFileInfo);

    const audioPlayerModule = useModule('AudioPlayer');
    const localFilesModule = useModule('LocalDesktopFiles');
    const msFilesModule = useModule('MSFiles');

    const fileTitleHover = useHover();

    const [newTimestamp, setNewTimestamp] = useState<{timestamp: string} | null>(null);

    const attributesBlocksProps: AttributesBlocksProps = {
        values: fileInfo?.attributes,
        parent_id: props.file_id,
        parent_type: 'File',
        workspace_id: props.workspace_id,
        refresh: refreshFileInfo,
    };

    const attributesBlocksHookState = useAttributesBlocksState(attributesBlocksProps);

    const [comments, refreshComments] = useSelectorWithRefresh(
        'getCommentsAndRepliesForParent',
        {parent_id: props.file_id, parent_type: 'File'},
        [],
        {workspace_id: props.workspace_id}
    );

    const regions: RegionWithComment[] = useMemo(() => {
        return [
            ...getRegionsForComments(comments),
            ...parseTimestampsWithRegions(fileInfo?.attributes?.chord_lyrics || '').map(t => ({
                parsedTimestamp: t,
                comment: undefined,
            })),
        ]
    }, [comments, fileInfo?.attributes.chord_lyrics, fileInfo?.attributes.description]);

    useEffect(() => {
        if (fileInfo) {
            // audioPlayerModule.playFile(fileInfo);
        }
    }, [fileInfo]);

    const playbackStatus = audioPlayerModule.useAudioPlaybackStatus();

    const isMobile = useIsMobile();
    const isWideScreen = useIsWideScreen();
    // const localFilesModule = useModule('LocalDesktopFiles');

    const modals = useModals();

    const selectors = useSelectors();

    const actions = {
        updateFileName: useAction('updateFileName'),
        deleteFile: useAction('deleteFile'),
        starObject: useAction('starObject'),
        unstarObject: useAction('unstarObject'),
        getFileInfoWithRaw: selectors.getFileInfoWithRaw,
    };

    const kyselyModule = useModule('Kysely');
    const currentUser = useCurrentUser();

    const [waveformAndDuration] = useSelectorWithRefresh('getFileWaveformAndDuration', {file_id: fileInfo?.id}, null, {workspace_id: fileInfo?.workspace_id || ''})

    if (loading || !fileInfo) {
        return (
            'Loading'
        );
    }

    if (error) {
        return (
            'Error fetching file'
        );
    }

    const isCurrentFile = playbackStatus?.fileInfo.id === fileInfo.id;
    const isPlaying = isCurrentFile && playbackStatus?.isPlaying;

    let openInDAWButton: React.ReactNode;
    // const dawApplicationName = localFilesModule?.isDAWProject(fileInfo.path);
    // if (dawApplicationName) {
    //     openInDAWButton = (
    //         <button
    //             onClick={() => {
    //                 localFilesModule?.openDAWProject(fileInfo.path);
    //             }}
    //         >
    //             Open in {dawApplicationName}
    //         </button>
    //     )
    // }

    const playIcon = isAudioFile(fileInfo.name) && (
        <PlayIcon
            fileInfo={fileInfo}
        />
    );

    const editFileNameButton = (
        <IconPencil
            className='pointer'
            style={{marginLeft: '15px'}}
            size={16}
            onClick={() => {
                let fileName = fileInfo.name;
                let suffix = '';
                const lastPeriodIndex = fileInfo.name.lastIndexOf('.');
                if (lastPeriodIndex !== -1) {
                    fileName = fileInfo.name.substring(0, lastPeriodIndex);
                    suffix = fileInfo.name.substring(lastPeriodIndex);
                }

                modals.openModal('TextInputModal', {
                    title: 'Edit file',
                    inputName: 'File name',
                    initialValue: fileName,
                    suffix,
                    onSubmit: async (fileName) => {
                        const fullName = `${fileName}${suffix}`;
                        setFileInfo(prev => {
                            if (!prev) {
                                return prev;
                            }
                            return {
                                ...prev,
                                name: fullName,
                            };
                        });

                        await actions.updateFileName({file_id: fileInfo.id, new_name: fullName}, {workspace_id: fileInfo.workspace_id});
                        refreshFileInfo();
                    },
                });
            }}
        />
    );

    const starElement = fileInfo.isStarred ? (
        <IconStarFilled
            style={{display: 'inline-block', marginRight: '5px', cursor: 'pointer'}}
            color='gold'
            opacity={0.8}
            size={18}
            onClick={async () => {
                modals.openModal('ConfirmationModal', {
                    title: 'Remove star from file?',
                    message: 'The file will no longer appear in your starred files.',
                    confirmLabel: 'Remove',
                    onConfirm: async () => {
                        await actions.unstarObject({parent_id: fileInfo.id, parent_type: 'File'}, {workspace_id: fileInfo.workspace_id});
                        refreshFileInfo();
                    },
                });
            }}
        />
    ) : (
        <IconStar
            style={{display: 'inline-block', marginRight: '5px', cursor: 'pointer'}}
            opacity={0.5}
            size={18}
            onClick={async () => {
                await actions.starObject({parent_id: fileInfo.id, parent_type: 'File'}, {workspace_id: fileInfo.workspace_id});
                refreshFileInfo();
            }}
        />
    );

    return (
        <div className={isWideScreen ? 'file-view-page-desktop' : 'file-view-page-mobile'}>
            <div className='file-header-section'>
                <div className='file-detail'>
                    <div style={{marginLeft: '5px'}}>
                        {starElement}
                        {localFilesModule && <localFilesModule.openWithFileHelpers.OpenWithComponent
                            fileId={fileInfo.id}
                            relativePath={fileInfo.relative_path}
                            workspaceId={fileInfo.workspace_id}
                        />}
                    </div>
                </div>
                <h2
                    ref={fileTitleHover.ref}
                    style={{
                        margin: '10px'
                    }}
                >
                    {!isMobile && fileInfo.name}
                    {fileTitleHover.hovered && editFileNameButton}
                </h2>

                {/* {fileInfo.earliest_known_modified_at && (
                    <div>
                        Last updated: {new Date(fileInfo.earliest_known_modified_at).toLocaleString()}
                    </div>
                )} */}

                <hr />

                <div>
                    {openInDAWButton}
                </div>
            </div>

            {!isWideScreen && isAudioFile(fileInfo.name) && (
                <div style={{
                    position: 'sticky',
                    top: '59px',
                    zIndex: 10,
                    backgroundColor: 'var(--mantine-color-body)',
                    padding: '10px',
                }}>
                    {playIcon}
                    {waveformAndDuration?.duration && waveformAndDuration?.waveform_data && (
                        <WavesurferWithHook
                            height={100}
                            isCurrentFile={isCurrentFile}
                            isPlaying={isCurrentFile && isPlaying}
                            onTimestampAction={(timestamp, action) => {
                                if (action === 'comment') {
                                    setNewTimestamp({timestamp});
                                }
                            }}
                            duration={waveformAndDuration.duration}
                            peaks={waveformAndDuration.waveform_data}
                            regions={regions}
                            onSeek={seconds => {
                                audioPlayerModule.playAndSeek(fileInfo, seconds);
                            }}
                        />
                    )}
                </div>
            )}

            <div className='main-content-wrapper'>
                <div className='file-detail'>
                    {isVideoFile(fileInfo.name) && (
                        <div>
                            <VideoPlayer
                                fileInfo={fileInfo}
                                timestamp={videoFileTimestamp}
                            />
                        </div>
                    )}
                    {isWideScreen && isAudioFile(fileInfo.name) && (
                        <div style={{
                            position: 'sticky',
                            zIndex: 10,
                            backgroundColor: 'var(--mantine-color-body)',
                            padding: '10px',
                        }}>
                            {playIcon}
                            {waveformAndDuration?.duration && waveformAndDuration?.waveform_data && (
                                <WavesurferWithHook
                                    height={100}
                                    isCurrentFile={isCurrentFile}
                                    isPlaying={isCurrentFile && isPlaying}
                                    onTimestampAction={(timestamp, action) => {
                                        if (action === 'comment') {
                                            setNewTimestamp({timestamp});
                                        }
                                    }}
                                    duration={waveformAndDuration.duration}
                                    peaks={waveformAndDuration.waveform_data}
                                    regions={regions}
                                    onSeek={seconds => {
                                        audioPlayerModule.playAndSeek(fileInfo, seconds);
                                    }}
                                />
                            )}
                        </div>
                    )}
                    <AttributesBlocksHeader
                        {...attributesBlocksProps}
                        hookState={attributesBlocksHookState}
                    // dotMenuItems={[
                    // ...attributesBlocksProps.dotMenuItems,
                    //     <Menu.Item
                    //         key='edit-project-name'
                    //         leftSection={<IconPencil size={14} />}
                    //         onClick={() => {

                    //         }}
                    //     >
                    //         Rename Project
                    //     </Menu.Item>,
                    // ]}
                    />

                    <div className='attributes-content'>
                        <AttributesBlocksBody
                            {...attributesBlocksProps}
                            hookState={attributesBlocksHookState}
                            // fileInfo={fileInfo}
                        />
                    </div>
                </div>

                <div className='comment-sidebar'>
                <CommentSection
                    variant='full'
                    newTimestamp={newTimestamp}
                    parent_id={props.file_id}
                    parent_type='File'
                    refreshComments={refreshComments}
                    clickedTimestamp={async (timestamp) => {
                        if (isAudioFile(fileInfo.name)) {
                            if (!isCurrentFile) {
                                const audio = (window as any).audioElement as HTMLAudioElement | undefined;
                                if (audio) {
                                    audio.pause();
                                }

                                audioPlayerModule.playFile(fileInfo);
                                await new Promise(r => setTimeout(r, 1000)); // TODO: this can be a better UX
                            }

                            const seconds = getSecondsFromTimestamp(timestamp);
                            audioPlayerModule.seekToSeconds(seconds);
                        }

                        if (isVideoFile(fileInfo.name)) {
                            setVideoFileTimestamp({timestamp});
                        }
                    }}
                    onInitialFocus={() => {
                        if (!isCurrentFile) {
                            return '';
                        }

                        // copied this from somewhere else. this should instead be exposed by the audio player module
                        const audio = (window as any).audioElement as HTMLAudioElement | undefined;
                        if (!audio) {
                            return '';
                        }

                        const currentTime = audio.currentTime;
                        const minutes = Math.floor(currentTime / 60);
                        const seconds = Math.floor(currentTime % 60);

                        const timestamp = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
                        return `${timestamp} `;
                    }}
                />
                </div>
            </div>
        </div>
    );
};

const VideoPlayer = ({fileInfo, timestamp}: {fileInfo: DBObjects['File']; timestamp: {timestamp: string} | null}) => {
    const fileClient = useModule('MSFiles').client();
    const [src, setSrc] = useState('');

    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        fileClient.getFileSrc(fileInfo).then(setSrc);
    }, [fileInfo]);

    useEffect(() => {
        if (!timestamp) {
            return;
        }

        const seconds = getSecondsFromTimestamp(timestamp.timestamp);
        const video = videoRef.current;
        if (!video) {
            return;
        }

        video.currentTime = seconds;
    }, [timestamp]);

    return (
        <div>
            {src && (
                <video
                    ref={videoRef}
                    controls
                    autoPlay
                    playsInline
                    style={{maxWidth: '100%', width: '800px'}}
                >
                    <source
                        src={src}
                        type={getVideoMimeTypeFromFileName(fileInfo.name)}
                    />
                </video>
            )}
        </div>
    );
};
